import React, { useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { PlayingCard } from "../PlayingCard";
import { DropZone } from "../DropZone";
import { playerInitial } from "../../lib/playerColors";
import { sortGroupCards, RANK_ORDER, RANK_ORDER_HIGH_ACE } from "../../lib/cardSort";
import { t, useLang } from "../../lib/i18n";
import { useDesktop } from "../../lib/desktop";
import { useGroupOrder } from "../../lib/tablePacking";

// Rank order for a sequence, considering both existing combo cards and pending cards
// (needed to detect K+A high-ace sequences before they're committed to the server).
function seqOrder(combo, pendingCards = []) {
  const all = [...combo.cards, ...pendingCards].filter((c) => !c.is_joker);
  const hasAce  = all.some((c) => c.rank === "A");
  const hasKing = all.some((c) => c.rank === "K");
  return hasAce && hasKing ? RANK_ORDER_HIGH_ACE : RANK_ORDER;
}

// Returns true if a non-Joker pending card should appear to the LEFT of the sequence.
function isLeftPendingCard(card, combo, pendingCards = []) {
  if (card.is_joker || combo.kind !== "sequence") return false;
  const firstNonJoker = combo.cards.find((c) => !c.is_joker);
  if (!firstNonJoker) return false;
  const order = seqOrder(combo, pendingCards);
  return order.indexOf(card.rank) < order.indexOf(firstNonJoker.rank);
}

export const TableCombinations = ({
  game,
  me,
  isMyTurn,
  takenDiscardCard,
  pendingGroups,
  pendingAdditions = [],
  pendingReplacements = [],
  recoveredJokers = [],
  committedAdditions = [],
  myHandRaw,
  playerColorMap = {},
  activeDropComboId = null,
  onRemoveGroup,
  onAddToPendingGroup,
  onRemovePendingAddition,
  onRemovePendingReplacement,
  onUndoAllComboChanges,
  room = null,
}) => {
  useLang();
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);

  // The groups in the order that uses the most of the screen, players mixed (lib/tablePacking.js): worked out when
  // the turn changes, and once a set breaking has left. Each keeps its seat, for the order within a row.
  const seated = game.players.flatMap((p) => (p.combinations || []).map((combo) => ({ p, combo })))
    .map((entry, seat) => ({ ...entry, seat }));
  const order = useGroupOrder({
    rootRef,
    turnKey: `${game.round_number}|${game.current_player_id}`,
    waiting: seated.some((e) => e.combo.breaking),
    room,
  });
  const place = new Map(order.map((id, i) => [id, i]));
  const placed = (e) => place.get(e.combo.id) ?? order.length + e.seat;
  const entries = seated.slice().sort((a, b) => placed(a) - placed(b));

  // A computer's large screen: bigger cards, and headers to match (lib/desktop.js).
  const big = useDesktop();
  const CARD = big ? "xs-lg" : "xs";
  // myHandRaw is the server hand and does NOT include takenDiscardCard (which is
  // client-side only until committed). We must search both pools so that a pending
  // group containing the staged discard card shows all its cards.
  const cardPool = takenDiscardCard ? [...myHandRaw, takenDiscardCard] : myHandRaw;
  const sortedById = (group) => {
    const cards = group.card_ids.map((cid) => cardPool.find((c) => c.id === cid)).filter(Boolean);
    return group.kind === "set" ? sortGroupCards("set", cards) : cards;
  };


  const additionsByCombo = {};
  pendingAdditions.forEach((a, idx) => {
    const cards = a.card_ids.map((cid) => myHandRaw.find((c) => c.id === cid)).filter(Boolean);
    additionsByCombo[a.combination_id] = {
      idx,
      cards,
      count: a.card_ids.length,
      joker_position: a.joker_position,
      joker_sides: a.joker_sides || {},
    };
  });

  // Preview additions from OTHER players currently preparing their Nemna3/Nefrech
  const previewAddsByCombo = {};
  (game?.players || []).forEach((p) => {
    if (p.user_id === me?.user_id) return;
    (p.preview_state?.additions || []).forEach((a) => {
      if (!previewAddsByCombo[a.combination_id]) {
        previewAddsByCombo[a.combination_id] = { playerName: p.name, cards: a.cards || [] };
      }
    });
  });

  const replacementsByJoker = {};
  const replacementsByCombo = {};
  pendingReplacements.forEach((r, idx) => {
    const replCard = myHandRaw.find((c) => c.id === r.replacement_card_id);
    const addCard = r.additional_card_id
      ? myHandRaw.find((c) => c.id === r.additional_card_id)
      : null;
    replacementsByJoker[r.joker_card_id] = { idx, replCard, addCard };
    replacementsByCombo[r.combination_id] = { idx, replCard, addCard };
  });

  const recoveryByCombo = {};
  recoveredJokers.forEach((r) => {
    recoveryByCombo[r.combination_id] = r;
  });

  const committedByCombo = {};
  committedAdditions.forEach((a) => {
    committedByCombo[a.combination_id] = a;
  });

  return (
    // On a phone the groups are inline blocks: they flow around the column's room (lib/tableRoom.js).
    <div ref={rootRef} className="-ml-1.5 pt-1 sm:ml-0 sm:p-1 sm:flex sm:flex-wrap sm:gap-1.5 sm:content-start">

      {/* ── Posed combinations ─────────────────────────────────────────── */}
      {entries.map(({ p, combo, seat }) => {
          const isOwn       = p.user_id === me?.user_id;
          const isLocked    = !!combo.locked;
          const breaking    = combo.breaking; // a completed set on its way out (BrokenSets.jsx): wait, crack, gone
          const pending     = additionsByCombo[combo.id];
          const hasRepl     = !!replacementsByCombo[combo.id];
          const previewAdd  = previewAddsByCombo[combo.id];
          const color       = playerColorMap[p.user_id];
          const isDragOver  = !isLocked && activeDropComboId === combo.id;

          const borderStyle = breaking === "crack"
            ? { borderColor: "#fde68a", boxShadow: "0 0 0 2px rgba(253,230,138,0.85), 0 0 26px rgba(251,191,36,0.6)" }
            : isDragOver
            ? { borderColor: "#fbbf24", boxShadow: "0 0 0 2px #fbbf24, 0 0 28px rgba(251,191,36,0.55)" }
            : previewAdd
            ? { borderColor: "rgba(251,191,36,0.5)", boxShadow: "0 0 0 1px rgba(251,191,36,0.3), 0 0 20px rgba(251,191,36,0.15)" }
            : pending
            ? {}
            : hasRepl
            ? {}
            : color
            ? { borderColor: color.border, boxShadow: `0 2px 14px ${color.glow}` }
            : {};

          return (
            <DropZone
              key={`${p.user_id}-${combo.id}`}
              id={`combo-${combo.id}`}
              data={{ type: "combo", owner_id: p.user_id, combo_id: combo.id, locked: isLocked }}
              data-combo-id={combo.id}
              data-combo-seat={seat}
              motionProps={{
                // Glides to its new place when the table is arranged; its cards follow it.
                layout: reduceMotion ? false : "position",
                initial: false,
                animate: { scale: isDragOver ? 1.03 : breaking === "gone" ? 0.95 : 1 },
                transition: { layout: { type: "spring", stiffness: 320, damping: 32 }, scale: { duration: 0.15 } },
              }}
              className={[
                "inline-block align-top ml-1.5 mb-1.5 sm:m-0 max-w-[var(--table-widest,100%)] sm:max-w-none",
                "relative rounded-xl overflow-hidden border transition-[border-color,box-shadow,opacity] duration-150",
                isOwn
                  ? "bg-gradient-to-b from-emerald-950/70 to-slate-950/80"
                  : "bg-gradient-to-b from-slate-900/70 to-slate-950/80",
                isLocked && !breaking ? "opacity-60 grayscale-[25%]" : "",
                breaking === "gone" ? "opacity-0" : "",
                pending    ? "border-emerald-400/45 shadow-[0_0_16px_rgba(52,211,153,0.18)]" : "",
                hasRepl    ? "border-fuchsia-400/35 shadow-[0_0_14px_rgba(217,70,239,0.15)]" : "",
                previewAdd ? "border-amber-400/45 shadow-[0_0_16px_rgba(251,191,36,0.18)]" : "",
              ].filter(Boolean).join(" ")}
              style={borderStyle}
              activeClassName=""
              testId={`combo-${combo.id}${isLocked ? "-locked" : ""}`}
            >

              {/* ── Header ───────────────────────────────────────────────── */}
              <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-1 border-b border-white/[0.06]">
                <div
                  className={`${big ? "w-5 h-5 text-[9px]" : "w-4 h-4 text-[7px]"} rounded-full flex-shrink-0 flex items-center justify-center font-bold`}
                  style={color
                    ? { background: color.bg, color: color.hex, boxShadow: `0 0 0 1.5px ${color.border}` }
                    : { background: "rgba(100,116,139,0.5)", color: "#CBD5E1" }
                  }
                >
                  {playerInitial(p)}
                </div>
                <span
                  className={`${big ? "text-[12px]" : "text-[10px]"} font-semibold truncate leading-none min-w-0 flex-1`}
                  style={color ? { color: color.hex } : { color: "#CBD5E1" }}
                >
                  {isOwn ? t("common.me") : p.name.slice(0, 5)}
                </span>
                <span lang="fr" className={`${big ? "text-[10px]" : "text-[8px]"} px-1.5 py-px rounded font-bold uppercase flex-shrink-0 ${
                  combo.kind === "set"
                    ? "bg-violet-500/20 text-violet-300"
                    : "bg-sky-500/20 text-sky-300"
                }`}>
                  {combo.kind === "set" ? t("table.set") : t("table.seq")}{isLocked && " 🔒"}
                </span>
                <span className={`${big ? "text-[12px]" : "text-[10px]"} font-bold text-amber-300 tabular-nums flex-shrink-0`}>
                  {combo.points}
                </span>
              </div>

              {/* ── Cards ────────────────────────────────────────────────── */}
              <div className="px-2 pt-1 pb-1">
                <motion.div
                  className="flex gap-1 items-end flex-wrap"
                  layout
                  data-breaking={breaking ? combo.id : undefined}
                  // The layer's cards have taken over, exactly in their place.
                  style={breaking && breaking !== "wait" ? { visibility: "hidden" } : undefined}
                >

                  {/* Cards that go to the LEFT of the sequence (Joker joker_position=left, or non-Joker with lower rank) */}
                  <AnimatePresence>
                    {pending && combo.kind === "sequence" &&
                      pending.cards
                        .filter((c) => c.is_joker
                          ? (pending.joker_sides[c.id] ?? pending.joker_position) === "left"
                          : isLeftPendingCard(c, combo, pending.cards)
                        )
                        .sort((a, b) => {
                          if (a.is_joker || b.is_joker) return 0;
                          return seqOrder(combo, pending.cards).indexOf(a.rank) - seqOrder(combo, pending.cards).indexOf(b.rank);
                        })
                        .map((c) => (
                          <motion.div
                            key={`pending-${c.id}`}
                            initial={{ scale: 0.7, opacity: 0, x: 6 }}
                            animate={{ scale: 1, opacity: 0.82, x: 0 }}
                            exit={{ scale: 0.7, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 22 }}
                            className="relative ring-2 ring-emerald-400/65 rounded-lg shadow-[0_0_8px_rgba(52,211,153,0.38)]"
                            data-testid={`pending-addition-${c.id}`}
                          >
                            <PlayingCard card={c} size={CARD} disabled />
                            <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-emerald-500 rounded-full flex items-center justify-center text-[8px] text-white font-bold shadow-md">+</div>
                          </motion.div>
                        ))
                    }
                  </AnimatePresence>

                  {(combo.kind === "set" ? sortGroupCards("set", combo.cards) : combo.cards).map((c, idx) =>
                    // Not a drop zone of its own: a card dropped anywhere on the
                    // group takes the Joker's place when it fits (Game.jsx). The
                    // Joker lights up while the group is aimed at.
                    c.is_joker ? (
                      <div
                        key={c.id}
                        className={`relative rounded-lg transition-shadow ${
                          replacementsByJoker[c.id]
                            ? "ring-2 ring-emerald-400/65 shadow-[0_0_8px_rgba(52,211,153,0.45)]"
                            : isDragOver
                            ? "ring-2 ring-fuchsia-300 shadow-[0_0_18px_rgba(217,70,239,0.8)]"
                            : "ring-2 ring-fuchsia-500/55 shadow-[0_0_8px_rgba(217,70,239,0.35)]"
                        }`}
                        data-testid={`joker-${c.id}`}
                      >
                        <motion.div
                          layout
                          layoutId={`card-${c.id}`}
                          initial={{ scale: 0.6, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.6, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 280, damping: 20 }}
                          style={{ zIndex: idx, lineHeight: 0 }}
                        >
                          <PlayingCard card={c} size={CARD} disabled />
                        </motion.div>
                        <div className={`absolute -top-1.5 -right-1.5 w-3.5 h-3.5 flex items-center justify-center text-[7px] text-white rounded-full shadow-md ${
                          replacementsByJoker[c.id] ? "bg-emerald-500" : "bg-fuchsia-600 animate-pulse"
                        }`}>
                          {replacementsByJoker[c.id] ? "✓" : "★"}
                        </div>
                      </div>
                    ) : (
                      <motion.div
                        key={c.id}
                        layout
                        layoutId={`card-${c.id}`}
                        initial={{ scale: 0.6, opacity: 0, rotateY: 180 }}
                        animate={{ scale: 1, opacity: 1, rotateY: 0 }}
                        exit={{ scale: 0.6, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 280, damping: 20 }}
                        style={{ zIndex: idx, lineHeight: 0 }}
                        className={combo.added_card_ids?.includes(c.id) ? "ring-2 ring-emerald-400/60 rounded-lg shadow-[0_0_6px_rgba(52,211,153,0.3)]" : ""}
                        data-break-card={breaking ? c.id : undefined}
                      >
                        <PlayingCard card={c} size={CARD} disabled />
                      </motion.div>
                    )
                  )}

                  {/* Cards that go to the RIGHT of the sequence, or any card on a set */}
                  <AnimatePresence>
                    {pending && pending.cards
                      .filter((c) => c.is_joker
                        ? !(combo.kind === "sequence" && (pending.joker_sides[c.id] ?? pending.joker_position) === "left")
                        : !(combo.kind === "sequence" && isLeftPendingCard(c, combo, pending.cards))
                      )
                      .sort((a, b) => {
                        if (combo.kind !== "sequence") return 0;
                        if (a.is_joker || b.is_joker) return 0;
                        return seqOrder(combo, pending.cards).indexOf(a.rank) - seqOrder(combo, pending.cards).indexOf(b.rank);
                      })
                      .map((c) => (
                        <motion.div
                          key={`pending-${c.id}`}
                          initial={{ scale: 0.7, opacity: 0, x: -6 }}
                          animate={{ scale: 1, opacity: 0.82, x: 0 }}
                          exit={{ scale: 0.7, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 300, damping: 22 }}
                          className="relative ring-2 ring-emerald-400/65 rounded-lg shadow-[0_0_8px_rgba(52,211,153,0.38)]"
                          data-testid={`pending-addition-${c.id}`}
                        >
                          <PlayingCard card={c} size={CARD} disabled />
                          <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-emerald-500 rounded-full flex items-center justify-center text-[8px] text-white font-bold shadow-md">+</div>
                        </motion.div>
                      ))}
                  </AnimatePresence>

                  <AnimatePresence>
                    {previewAdd && previewAdd.cards.map((c, i) => (
                      <motion.div
                        key={`preview-add-${c.id ?? i}`}
                        initial={{ scale: 0.7, opacity: 0, x: -6 }}
                        animate={{ scale: 1, opacity: 0.72, x: 0 }}
                        exit={{ scale: 0.7, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        className="relative ring-2 ring-amber-400/65 rounded-lg shadow-[0_0_8px_rgba(251,191,36,0.38)]"
                      >
                        <PlayingCard card={c} size={CARD} disabled />
                        <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center text-[8px] text-white font-bold shadow-md">+</div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                </motion.div>

                {/* Preview addition label — who is staging this */}
                <AnimatePresence>
                  {previewAdd && (
                    <motion.div
                      initial={{ opacity: 0, y: 3 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 3 }}
                      className="flex items-center gap-1 mt-1.5 pt-1 border-t border-amber-400/10"
                    >
                      <span className="text-[8px] text-amber-400/80 font-medium">
                        {t("table.preparing", { name: previewAdd.playerName })}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Cancel addition */}
                <AnimatePresence>
                  {isMyTurn && pending && (
                    <motion.div
                      initial={{ opacity: 0, y: 3 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 3 }}
                      className="flex justify-end mt-1.5"
                    >
                      <button
                        type="button"
                        onClick={() => onRemovePendingAddition?.(pending.idx)}
                        className="flex items-center gap-1 text-[9px] text-rose-400 hover:text-rose-300 px-1.5 py-0.5 rounded-md hover:bg-rose-500/12 transition-colors"
                        data-testid={`cancel-pending-addition-${combo.id}`}
                      >
                        <X className="w-2.5 h-2.5" />
                        {t("table.cancelAdd")}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Pending joker replacement */}
                <AnimatePresence>
                  {isMyTurn && replacementsByCombo[combo.id] && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-fuchsia-500/12"
                      data-testid={`pending-replacement-${combo.id}`}
                    >
                      <span className="text-[8px] text-fuchsia-400/80 font-medium shrink-0">{t("table.swap")}</span>
                      {replacementsByCombo[combo.id].replCard && (
                        <div className="relative opacity-82 ring-1 ring-emerald-400/55 rounded-lg">
                          <PlayingCard card={replacementsByCombo[combo.id].replCard} size={CARD} disabled />
                        </div>
                      )}
                      {replacementsByCombo[combo.id].addCard && (
                        <div className="relative opacity-82 ring-1 ring-slate-400/35 rounded-lg">
                          <PlayingCard card={replacementsByCombo[combo.id].addCard} size={CARD} disabled />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => onRemovePendingReplacement?.(replacementsByCombo[combo.id].idx)}
                        className="ml-auto flex items-center gap-1 text-[9px] text-rose-400 hover:text-rose-300 px-1.5 py-0.5 rounded-md hover:bg-rose-500/12 transition-colors"
                        data-testid={`cancel-pending-replacement-${combo.id}`}
                      >
                        <X className="w-2.5 h-2.5" />
                        {t("common.cancel")}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Single undo button for all changes on this combo this turn */}
                <AnimatePresence>
                  {isMyTurn && (committedByCombo[combo.id] || recoveryByCombo[combo.id] || combo.has_uncommitted_mods) && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-white/5"
                      data-testid={`undo-combo-changes-${combo.id}`}
                    >
                      <span className="text-[9px] text-slate-400">
                        {committedByCombo[combo.id] && recoveryByCombo[combo.id]
                          ? t("table.addedAndJoker")
                          : committedByCombo[combo.id]
                          ? t("table.nAdded", { n: committedByCombo[combo.id].card_ids.length })
                          : recoveryByCombo[combo.id]
                          ? t("table.jokerRecovered")
                          : t("table.modsPending")}
                      </span>
                      <button
                        type="button"
                        onClick={() => onUndoAllComboChanges?.(combo.id, p.user_id)}
                        className="flex items-center gap-1 text-[9px] text-rose-400 hover:text-rose-300 px-1.5 py-0.5 rounded-md hover:bg-rose-500/12 transition-colors"
                      >
                        <X className="w-2.5 h-2.5" />
                        {t("table.undo")}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </DropZone>
          );
      })}

      {/* ── Pending groups (staging) ───────────────────────────────────── */}
      {pendingGroups.map((g, idx) => (
        <DropZone
          key={idx}
          id={`pending-group-${idx}`}
          data={{ type: "pending-group", groupIdx: idx }}
          className="inline-block align-top ml-1.5 mb-1.5 sm:m-0 max-w-[var(--table-widest,100%)] sm:max-w-none relative rounded-2xl border border-amber-400/28 bg-gradient-to-b from-amber-950/55 to-slate-950/75 shadow-[0_0_20px_rgba(251,191,36,0.1),0_2px_14px_rgba(0,0,0,0.45)] transition-all duration-200"
          activeClassName="border-amber-300/55 shadow-[0_0_26px_rgba(251,191,36,0.26)]"
        >

          {/* Header */}
          <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-1 border-b border-amber-400/10">
            <span lang="fr" className={`${big ? "text-[10px]" : "text-[8px]"} px-1.5 py-px rounded font-bold uppercase ${
              g.kind === "set"
                ? "bg-violet-500/20 text-violet-300"
                : "bg-sky-500/20 text-sky-300"
            }`}>
              {g.kind === "set" ? t("table.set") : t("table.seq")}
            </span>
            <span className={`${big ? "text-[12px]" : "text-[10px]"} font-bold text-amber-300 tabular-nums ml-auto`}>{g.points || 0}</span>
            <button
              type="button"
              onClick={() => onRemoveGroup(idx)}
              className="w-4 h-4 flex items-center justify-center rounded-full text-rose-400 hover:text-rose-300 hover:bg-rose-500/15 transition-colors"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>

          {/* Cards */}
          <div className="px-2 pt-1 pb-1">
            <div
              className="flex gap-1 items-end flex-wrap"
              data-testid={`pending-group-${idx}`}
            >
              {sortedById(g).map((c) => (
                <div key={c.id} className="leading-[0]">
                  <PlayingCard card={c} size={CARD} disabled />
                </div>
              ))}
            </div>
          </div>
        </DropZone>
      ))}

      {/* ── New combination: a plain bar across the table. It is only the visible sign:
           cards dropped anywhere on the table outside a group land here too (boardCollision). ── */}
      {isMyTurn && !me?.eliminated && (game.phase === "acting" || takenDiscardCard) && (me?.turns_played ?? 1) >= 1 && (
        <DropZone
          id="new-group-table"
          data={{ type: "new-group", kind: "auto" }}
          className={`basis-full w-[var(--table-widest,100%)] sm:w-full ml-1.5 mb-1.5 sm:m-0 align-top ${big ? "h-16" : "h-11"} inline-flex sm:flex items-center justify-center rounded-xl border border-dashed border-white/25 bg-transparent transition-all duration-200`}
          activeClassName="border-amber-300/70 bg-amber-400/[0.07] shadow-[0_0_18px_rgba(251,191,36,0.18)]"
          testId="drop-new-group"
        >
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/60 select-none pointer-events-none">
            {t("table.drop3")}
          </span>
        </DropZone>
      )}
    </div>
  );
};

export default TableCombinations;
